import type { BuildingEntityHandleBase } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import type { Plane } from "geom/planeTypes";
import type { PolyLine3 } from "geom/polyLine3";

/**
 * This file is a LEAF node in the code base. It only imports from geom.
 * In order to prevent cyclical dependencies, please do not add any import to this
 * file that is not from geom
 */

export const LUMBER_UNION_GAP = 0.025;

export interface LumberProfileDescriptor {
  readonly type: LumberProfileType;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly annotationLabel: string;
}

export interface LumberLengthDescriptor {
  readonly type: LumberLengthType;
  readonly name: string;
  readonly length: number;
}

export interface StandardLumberCounter {
  readonly profileDescriptor: LumberProfileDescriptor;
  readonly lengthDescriptor: LumberLengthDescriptor;
  readonly wasted: number[];
  count: number;
}

export interface LumberRail {
  readonly id: string;
  readonly handle: BuildingEntityHandleBase<"LUMBER">;
  readonly rail: PolyLine3;
  readonly nX: number;
  readonly nY: number;
  readonly nZ: number;
  readonly length: number;
  readonly clipPlanes?: Plane[] | null;
  readonly startExtension?: number | null;
  readonly endExtension?: number | null;
}

export interface LumberPiecesByProfile {
  readonly id: string;
  readonly generatingHandles: Set<DocEntityHandle | GeneratedEntityHandle>;
  readonly profileDescriptor: LumberProfileDescriptor;
  readonly railsByType: LumberRailsByType;
}

export interface WastedLumber {
  readonly profileDescriptor: LumberProfileDescriptor;
  totalLength: number;
  wastedLength: number;
  wastedVolume: number;
  percentage: number;
}

export type LumberRailsByType = { [type: string]: LumberRail[] };
export type StandardLumberCounterSet = { [lumberType: string]: StandardLumberCounter };
export type LumberPiecesByProfileSet = { [profileType: string]: LumberPiecesByProfile };
export type WastedLumberSet = { [profileType: string]: WastedLumber };

export enum LumberProfileType {
  SPF_2X4 = "SPF_2X4",
  SPF_2X6 = "SPF_2X6",
  SPF_2X8 = "SPF_2X8",
  SPF_2X10 = "SPF_2X10",
  SPF_2X12 = "SPF_2X12",
  CUSTOM = "CUSTOM",
}

export const LumberProfileTypeToName: { [key in LumberProfileType]: string } = {
  [LumberProfileType.SPF_2X4]: "2x4",
  [LumberProfileType.SPF_2X6]: "2x6",
  [LumberProfileType.SPF_2X8]: "2x8",
  [LumberProfileType.SPF_2X10]: "2x10",
  [LumberProfileType.SPF_2X12]: "2x12",
  [LumberProfileType.CUSTOM]: "Custom",
} as const;

export enum LumberLengthType {
  // L6 = "L6",
  L8 = "L8",
  L10 = "L10",
  L12 = "L12",
  L14 = "L14",
  L16 = "L16",
  L18 = "L18",
  L20 = "L20",
  L22 = "L22",
  L24 = "L24",
}

//TODO: add more profiles in the future
export enum SubstrateProfileType {
  PLYWOOD = "PLYWOOD",
  OSB = "OSB",
}

//TODO: expand span-ratings and plywood/osb types in the future
/* eslint-disable @typescript-eslint/naming-convention */
export enum SpanRating {
  "SR12" = "SR12",
  "SR16" = "SR16",
  "SR20" = "SR20",
  "SR24" = "SR24",
  "SR32/16" = "SR32/16", //"32/16" means max 32" spacing when used as roof sheathing, 16" max for subflooring
  "SR40/20" = "SR40/20", //"40/20" means max 40" spacing when used as roof sheathing, 20" max for subflooring
  "SR48/24" = "SR48/24", //"48/24" means max 48" spacing when used as roof sheathing, 24" max for subflooring
}

export enum PlywoodProfile {
  "5/16-u" = "5/16-u",
  "3/8-u" = "3/8-u",
  "15/32-u" = "15/32-u",
  "1/2-u" = "1/2-u",
  "19/32-u" = "19/32-u",
  "5/8-u" = "5/8-u",
  "3/4-u" = "3/4-u",
  "1/4-s" = "1/4-s",
  "11/32-s" = "11/32-s",
  "3/8-s" = "3/8-s",
  "15/32-s" = "15/32-s",
  "1/2-s" = "1/2-s",
  "19/32-s" = "19/32-s",
  "3/4-s" = "3/4-s",
  "5/8-s" = "5/8-s",
  "23/32-s" = "23/32-s",
}

export const PlywoodProfileToThickness: { [key in PlywoodProfile]: number } = {
  "5/16-u": 5 / 16,
  "3/8-u": 3 / 8,
  "15/32-u": 15 / 32,
  "1/2-u": 1 / 2,
  "19/32-u": 19 / 32,
  "5/8-u": 5 / 8,
  "3/4-u": 3 / 4,
  "1/4-s": 1 / 4,
  "11/32-s": 11 / 32,
  "3/8-s": 3 / 8,
  "15/32-s": 15 / 32,
  "1/2-s": 1 / 2,
  "19/32-s": 19 / 32,
  "3/4-s": 3 / 4,
  "5/8-s": 5 / 8,
  "23/32-s": 23 / 32,
} as const;

//add some OSB types
export enum OSBProfile {
  "3/8" = "3/8",
  "7/16" = "7/16",
  "15/32" = "15/32",
  "1/2" = "1/2",
  "19/32" = "19/32",
  "23/32" = "23/32",
  "3/4" = "3/4",
}

export const OSBProfileToThickness: { [key in OSBProfile]: number } = {
  "3/8": 3 / 8,
  "7/16": 7 / 16,
  "15/32": 15 / 32,
  "1/2": 1 / 2,
  "19/32": 19 / 32,
  "23/32": 23 / 32,
  "3/4": 3 / 4,
} as const;

/* eslint-enable @typescript-eslint/naming-convention */

export function lumberProfileDescriptorNew(
  type: LumberProfileType,
  name: string,
  width: number,
  height: number,
  annotationLabel: string = name,
): LumberProfileDescriptor {
  return {
    type,
    name,
    width,
    height,
    annotationLabel,
  };
}

export const LumberProfileCatalog: {
  [key in Exclude<LumberProfileType, LumberProfileType.CUSTOM>]: LumberProfileDescriptor;
} = {
  SPF_2X4: lumberProfileDescriptorNew(LumberProfileType.SPF_2X4, "2x4 SPF", 3.5, 1.5, "2x4"),
  SPF_2X6: lumberProfileDescriptorNew(LumberProfileType.SPF_2X6, "2x6 SPF", 5.5, 1.5, "2x6"),
  SPF_2X8: lumberProfileDescriptorNew(LumberProfileType.SPF_2X8, "2x8 SPF", 7.25, 1.5, "2x8"),
  SPF_2X10: lumberProfileDescriptorNew(LumberProfileType.SPF_2X10, "2x10 SPF", 9.25, 1.5, "2x10"),
  SPF_2X12: lumberProfileDescriptorNew(LumberProfileType.SPF_2X12, "2x12 SPF", 11.25, 1.5, "2x12"),
};

export interface SubstrateProfileBase<T extends SubstrateProfileType> {
  readonly substrateProfileType: T;
  readonly name: string;
  readonly spanRating: SpanRating;
  readonly thickness: number;
}

export interface Plywood extends SubstrateProfileBase<SubstrateProfileType.PLYWOOD> {
  plywoodProfile: PlywoodProfile;
}
export interface OSB extends SubstrateProfileBase<SubstrateProfileType.OSB> {
  osbProfile: OSBProfile;
}
